import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IQuotationProduct } from 'src/app/features/quotations/models/quotations.models';


export function numericOnlyValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const isValid = !control.value || (control.value && /^\d+$/.test(control.value));
    return isValid ? null : { numericOnly: true };
  };
}

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = emailRegex.test(control.value);
    return valid ? null : { invalidEmail: true };
  };
}

export function quantityValidator(product: IQuotationProduct,translate: TranslateService): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (isNaN(value) || value <= 0) {
      return { quantityInvalid: translate.instant('quotations_and_orders.quantityInvalid') };
    }

    if (product.min_qte !== undefined && product.min_qte !== null && value < product.min_qte) {
      return { quantityMin: translate.instant('quotations_and_orders.quantityMin', { min_qte: product.min_qte }) };
    }

    if (product.max_qte !== undefined && product.max_qte !== null && value > product.max_qte) {
      return { quantityMax: translate.instant('quotations_and_orders.quantityMax', { max_qte: product.max_qte }) };
    }

    // Valid quantity = min_qte + (multiple of increment)
    const minQte = product.min_qte ?? 0;
    if (product.increment !== undefined && product.increment !== null && product.increment > 0) {
      const remainder = (value - minQte) % product.increment;
      if (remainder !== 0) {
        return {
          quantityIncrement: translate.instant('quotations_and_orders.quantityIncrementMin', {
            min_qte: product.min_qte ?? 0,
            increment: product.increment,
          }),
        };
      }
    }

    return null; // No errors found
  };
}

export function customValidator(translate: TranslateService): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const increment = group.get('increment')?.value;
    const min_qte = group.get('min_qte')?.value;
    const max_qte = group.get('max_qte')?.value;

    const errors: any = {};

    if (min_qte != null && min_qte < 1) {
      errors.minQte = translate.instant('Catalogs.validators.min_qte_greater_equal_one');
    }

    if (increment != null && increment < 1) {
      errors.incrementMin = translate.instant('Catalogs.validators.increment_greater_equal_one');
    }

    if (min_qte != null && max_qte != null && min_qte >= max_qte) {
      errors.minMax = translate.instant('Catalogs.validators.min_less_than_max');
    }

    if (increment != null && min_qte != null && increment > min_qte) {
      errors.increment = translate.instant('Catalogs.validators.increment_less_than_or_equal_min');
    }

    return Object.keys(errors).length ? errors : null;
  };
}

// Control validator for at least one option must be added to the array
export function atLeastOneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return value && Array.isArray(value) && value.length > 0
      ? null
      : { atLeastOne: true };
  };
}

// Group validator for range validation
export function rangeValidator(minKey: string = 'min', maxKey: string = 'max'): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const min = group.get(minKey)?.value;
    const max = group.get(maxKey)?.value;

    if (min == null || max == null) {
      return null;
    }

    return parseFloat(min) >= parseFloat(max) ? { rangeInvalid: true } : null;
  };
}

// Group validator for range validation that allows empty max value (for last threshold)
export function rangeValidatorWithOptionalMax(minKey: string = 'min', maxKey: string = 'max'): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const min = group.get(minKey)?.value;
    const max = group.get(maxKey)?.value;

    if (min == null) {
      return null;
    }

    // If max is empty/null, it's valid (no upper limit)
    if (max == null || max === '') {
      return null;
    }

    return parseFloat(min) >= parseFloat(max) ? { rangeInvalid: true } : null;
  };
}

// Control validator for value must be included in the array
export function includesValidator(data: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Skip validation if value is empty — let `Validators.required` handle it
    if (!value || value.trim() === '') {
      return null;
    }

    // Validate only if value is not empty
    return data.includes(value) ? null : { notFound: true };
  };
}

export function requiredTrimValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return { required: true };
    }

    const trimmedValue = typeof control.value === 'string' ? control.value.trim() : control.value;

    if (!trimmedValue) {
      return { required: true };
    }

    return null;
  };
}

export function numericAndAlphaOnlyValidator (): ValidatorFn {
  return (control: AbstractControl) => {
    const isValid = !control.value || (control.value && /^[a-zA-Z0-9]+$/.test(control.value));
    return isValid ? null : { numericAndAlphaOnly: true };
  };
}

export function atLeastOneOfMultipleValidator(groupName: string, controlNames: string[]): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    if (!group || !controlNames || controlNames.length === 0) {
      return null;
    }

    const hasValue = controlNames.some(controlName => {
      const control = group.get(controlName);
      const value = control?.value;
      
      // Check if the value exists and is not empty
      if (value === null || value === undefined) {
        return false;
      }
      
      // For strings, check if trimmed value is not empty
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      
      // For arrays, check if array has items
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      // For other types, just check if truthy
      return !!value;
    });

    if (hasValue) {
      return null;
    }
    
    const errorKey = `AtLeastOneOfMultiple_${groupName}`;

    return { [errorKey]: true };
  };
}

export function positiveNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  // Allow empty values — leave "required" to the required validator
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return Number(value) > 0 ? null : { positiveNumber: true };
}

export function integerValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  // Allow empty values — leave "required" to the required validator
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numValue = Number(value);
  
  // Check if it's a valid number and an integer
  if (isNaN(numValue)) {
    return { integer: true };
  }

  return Number.isInteger(numValue) ? null : { integer: true };
}
